<template>
  <v-dialog v-model="isVisible" max-width="600px" @click:outside="close">
    <v-card class="useredit--card">
      <v-card-title class="dialog-title">
        <span class="headline">{{ competition ? $t("edit_competition") : $t("create_competition") }}</span>
      </v-card-title>
      <v-stepper v-model="step">
        <v-stepper-header>
          <template v-for="n in steps">
            <v-stepper-step :key="`${n}-step`" :complete="step > n" :step="n">
              {{ stepTitles[n - 1] }}
            </v-stepper-step>
            <v-divider v-if="n !== steps" :key="n"></v-divider>
          </template>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content key="1" step="1" class="px-0">
            <validation-observer ref="competitionObserver" v-slot="{ invalid }">
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col class="px-0" cols="12">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('title')"
                        rules="required"
                      >
                        <v-text-field
                          :error-messages="errors"
                          v-model="editedItem.competitionTitle"
                          :label="$t('title')"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col
                      class="pl-0 pl-0 col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-12"
                    >
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('start_date')"
                        rules="required"
                        ref="startdate"
                        vid="startdate"
                      >
                        <v-menu
                          ref="menu1"
                          :return-value.sync="startdate"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :error-messages="errors"
                              v-model="editedItem.startdate"
                              :label="$t('start_date')"
                              hint="MM/DD/YYYY format"
                              persistent-hint
                              readonly
                              prepend-icon="mdi-calendar"
                              v-bind="attrs"
                              @blur="
                                startdate = parseDate(editedItem.startdate)
                              "
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="startdate"
                            no-title
                            @input="$refs.menu1.save(startdate)"
                          ></v-date-picker>
                        </v-menu>
                      </validation-provider>
                    </v-col>
                    <v-col
                      class="pl-0 pl-0 col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-12"
                    >
                      <validation-provider
                        ref="dateend"
                        v-slot="{ errors }"
                        :name="$t('end_date')"
                        rules="required|date_after:@startdate"
                      >
                        <v-menu
                          ref="menu2"
                          :close-on-content-click="false"
                          :return-value.sync="enddate"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :error-messages="errors"
                              v-model="editedItem.enddate"
                              :label="$t('end_date')"
                              hint="MM/DD/YYYY format"
                              persistent-hint
                              readonly
                              prepend-icon="mdi-calendar"
                              v-bind="attrs"
                              @blur="enddate = parseDate(editedItem.enddate)"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="enddate"
                            no-title
                            @input="$refs.menu2.save(enddate)"
                          ></v-date-picker>
                        </v-menu>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions class="py-1">
                <v-spacer></v-spacer>
                <v-btn class="cancel-button px-3" text @click="close">
                  {{ $t("cancel") }}
                </v-btn>
                <v-btn
                  :class="{
                    'confirm-button px-5': !invalid,
                    'disabled px-5': invalid,
                  }"
                  :disabled="invalid"
                  text
                  @click="step++"
                >
                  {{ $t("continue") }}
                </v-btn>
              </v-card-actions>
            </validation-observer>
          </v-stepper-content>
          <v-stepper-content key="2" step="2" class="px-0">
            <v-card>
              <v-list-item
                multiple
                v-for="company in companies"
                :key="company.id"
                :value="company.id"
                :class="{
                  'v-list-item--active': isCompanySelected(company.id),
                }"
                @click="addCompany(company)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ company.value }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-card-actions>
                <v-btn color="blue darken-1" text @click="step--">{{
                  $t("back")
                }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn class="cancel-button px-3" text @click="close">
                  {{ $t("cancel") }}
                </v-btn>
                <v-btn
                  class="confirm-button px-5"
                  text
                  :disabled="editedItem.selectedCompanies.length === 0"
                  @click="step++"
                >
                  {{ $t("next") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
          <v-stepper-content step="3" key="3">
            <v-card>
              <v-list-item
                multi-line
                multipe
                class="my-1"
                v-for="badge in badgesList"
                :key="badge.id"
                :value="badge.id"
                :class="{ 'v-list-item--active': isBadgeSelected(badge.id) }"
                @click="() => {}"
              >
                <ValidationObserver v-slot="{ invalid }">
                  <div class="flex d-flex">
                    <v-list-item-avatar>
                      <v-img :src="badge.smallimage"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content>

                      <v-list-item-title class="d-grid">
                        <p class="d-inline-block text-truncate">{{badge.value}}</p>
                      </v-list-item-title>
                      <v-col cols="6" class="py-0">
                        <validation-provider
                            slim
                            v-slot="{ errors }"
                            :name="$t('points')"
                            rules="required|numeric|min_value:1"
                        >
                        <v-text-field
                          :error-messages="errors"
                          v-model="badge.points"
                          @keyup="removeBadge(badge)"
                          @change="removeBadge(badge)"
                          :label="$t('points')"
                        ></v-text-field>
                        </validation-provider>
                      </v-col>
                      <v-col cols="6" class="py-0">
                        <validation-provider
                            slim
                            v-slot="{ errors }"
                            :name="$t('limit')"
                            rules="numeric|min_value:1"
                        >
                        <v-text-field
                            :error-messages="errors"
                            v-model="badge.limit"
                            :label="$t('limit')"
                            @keyup="removeBadge(badge)"
                            @change="removeBadge(badge)"
                        ></v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-list-item-content>
                    <v-list-item-action class="justify-end">
                      <v-btn
                        @click="addBadge(badge)"
                        class="confirm-button px-3"
                        text
                        :disabled="invalid"
                      >
                        {{
                          isBadgeSelected(badge.id) ? $t("remove") : $t("add")
                        }}
                      </v-btn>
                    </v-list-item-action>
                  </div>
                </ValidationObserver>
              </v-list-item>

              <v-card-actions>
                <v-btn color="blue darken-1" text @click="step--">{{
                  $t("back")
                }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn class="cancel-button px-3" text @click="close">
                  {{ $t("cancel") }}
                </v-btn>
                <v-btn
                  class="confirm-button px-5"
                  @click="step++"
                  text
                  :disabled="editedItem.selectedBadges.length === 0"
                >
                  {{ $t("next") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
          <v-stepper-content key="4" step="4" class="px-0">
            <v-card elevation="0" class="px-4">
              <competition-info :enddate="editedItem.enddate" :startdate="editedItem.startdate" :competition-title="editedItem.competitionTitle" :badges="editedItem.selectedBadges" :companies="editedItem.selectedCompanies" />
              <v-card-actions>
                <v-btn color="blue darken-1" text @click="step--">{{
                  $t("back")
                }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn class="cancel-button px-3" text @click="close">
                  {{ $t("cancel") }}
                </v-btn>
                <v-btn class="confirm-button px-5" text @click="competition ? update() : save()">
                  {{ competition ? $t('save') : $t("create") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
  </v-dialog>
</template>
<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import CompetitionInfo from "@/components/CompetitionInfo.vue";
export default {
  name: "CreateCompetition",
  components: {
    CompetitionInfo,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
      required: true,
    },
    companies: {
      required: true,
    },
    badges: {
      required: true,
    },
    competition: {
      required: false
    }
  },
  data: (v) => ({
    menu1: false,
    menu2: false,
    competitionTitle: "",
    startdate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substring(0, 10),
    enddate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substring(0, 10),
    selectedCompanies: [],
    selectedBadges: [],
    steps: 4,
    step: 1,
    stepTitles: [
      v.$t("competitionInfo"),
      v.$t("companies"),
      v.$t("badges"),
      v.$t("confirmation"),
    ],
    editedItem: {
      competitionTitle: "",
      startdate: v.formatDate(
        new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10)
      ),
      enddate: v.formatDate(
        new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10)
      ),
      selectedCompanies: [],
      selectedBadges: [],
    },
    defaultItem: {
      competitionTitle: "",
      startdate: v.formatDate(
        new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10)
      ),
      enddate: v.formatDate(
        new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10)
      ),
      selectedCompanies: [],
      selectedBadges: [],
    },
    badgesList: v.badges.map(b => ({...b}))
  }),
  created() {
    this.competition && this.setCompetitionData(this.competition);
  },
  watch: {
    startdate(val) {
      this.editedItem.startdate = this.formatDate(val);
    },
    enddate(val) {
      this.editedItem.enddate = this.formatDate(val);
    },
  },
  methods: {
    removeBadge(item) {
      if (this.isBadgeSelected(item.id)) {
        this.editedItem.selectedBadges = [
          ...this.editedItem.selectedBadges.filter((el) => el.id !== item.id),
        ];
      }
    },
    setCompetitionData(competition) {
      this.startdate =new Date(new Date(competition.startdate) - new Date().getTimezoneOffset() * 60000).toISOString().substring(0,10);
      this.enddate =new Date(new Date(competition.enddate) - new Date().getTimezoneOffset() * 60000).toISOString().substring(0,10);

      competition.badges.forEach(( e ) => {
        for (const i in this.badgesList) {
          if (this.badgesList[i].id == e.id) {
            this.badgesList[i].limit = e.limit !== 0 ? e.limit : "";
            this.badgesList[i].points = e.points;
          }
        }
      });

      this.editedItem.competitionTitle = competition.title;
      this.editedItem.startdate = competition.startdate;
      this.editedItem.enddate = competition.enddate;
      this.editedItem.selectedCompanies = this.companies.filter( e=> competition.companies.includes(e.id));
      this.editedItem.selectedBadges = competition.badges.map(e => {
        return {id: e.id, limit: e.limit, points: e.points, smallimage: e.badge.smallimage, value: e.badge.name}
      });
    },
    addBadge(item) {
      if (this.isBadgeSelected(item.id)) {
        this.editedItem.selectedBadges = [
          ...this.editedItem.selectedBadges.filter((el) => el.id !== item.id),
        ];
      } else {
        this.editedItem.selectedBadges = [
          ...this.editedItem.selectedBadges,
          item,
        ];
      }
    },
    addCompany(item) {
      if (this.isCompanySelected(item.id)) {
        this.editedItem.selectedCompanies = [
          ...this.editedItem.selectedCompanies.filter((el) => el.id !== item.id),
        ];
      } else {
        this.editedItem.selectedCompanies = [
          ...this.editedItem.selectedCompanies,
          item,
        ];
      }
    },
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${month}/${day}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      const [month, day, year] = date.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    isBadgeSelected(id) {
      return (
        this.editedItem.selectedBadges.find((e) => e.id === id) !== undefined
      );
    },
    isCompanySelected(id) {
      return this.editedItem.selectedCompanies.find( e => e.id === id) !== undefined;
    },
    close() {
      this.resetData();
      this.$emit("close");
    },
    save() {
      const competition = {
        title: this.editedItem.competitionTitle,
        startdate: this.editedItem.startdate,
        enddate:this.editedItem.enddate,
        badges: this.editedItem.selectedBadges.map( e=> ({id: e.id, points: e.points, limit: e.limit ?? 0})),
        companies: this.editedItem.selectedCompanies.map( e=> e.id)
      }
      this.$emit("create-competition", competition);
      this.close();
    },
    update() {
      const competition = {
        id: this.competition.id,
        title: this.editedItem.competitionTitle,
        startdate: this.editedItem.startdate,
        enddate:this.editedItem.enddate,
        badges: this.editedItem.selectedBadges.map( e=> ({id: e.id, points: e.points, limit: e.limit ?? 0})),
        companies: this.editedItem.selectedCompanies.map( e=> e.id)
      }
      this.$emit("update-competition", competition);
      this.close();
    },
    resetData() {
      this.$refs.competitionObserver?.reset();
      this.editedItem = this.defaultItem;
      this.step = 1;
    },
  },
};
</script>
<style scoped>
.v-list-item--active {
  color: deeppink;
  border: 1px solid #ff1493;
}
.d-grid {
  display: grid;
}
</style>
